import _, { keys, get } from 'lodash';
import { createSelector } from 'reselect';
import { NON_RETIREMENT, RETIREMENT } from 'src/settings/settings';
import { formatUnixDate } from 'src/utils/dates/date-utility';
import { ApplicationState } from '../../types';
import { getRothConversionFundLimitMessage } from 'src/utils/funds/funds-utility';
import {
  getUserBPID,
  getUserBusinessPartyId,
} from '../../user/PersonalInformation/personalInformation.selectors';
import { getJustPendingAgreementIDs } from '../../pendingTransactions/pendingTransactions.selectors';

export const getUserFunds = ({ userFunds }: ApplicationState) => userFunds;

export const getExistingRMD = (state) => {
  return get(state, 'RMDSetting.existingRMDData') || [];
};

export const getUserFundsRaw = createSelector(
  [getUserFunds],
  (userFunds) => userFunds?.funds
);

/**
 * Simple selector to extract agreementMap that exists within user funds.
 */
export const getAgreementMap = createSelector(
  [getUserFunds],
  ({ agreementMap }) => agreementMap
);

export const getUserFundsByAgreementID = createSelector(
  [getUserFunds],
  (userFunds) => userFunds?.fundsByAgreementID || {}
);

export const getDisplayTradeDate = createSelector(
  [getUserFundsRaw],
  (funds) => funds?.[0]?.displayTradeDate
);

/**
 * Simple selector to extract agreementMap that exists within user funds.
 * @param state
 * @returns {retirementAgreements | Object}
 * FORMAT
 *
 * { TRADITIONAL_IRA: {
 *  agreementID(pin): "29944504"
 *   agreementType(pin): "TRADITIONAL_IRA"
 *   balanceAsOf(pin): 42666.85
 *   agreementNumber(pin): "1420005195-5"
 *   trpCode(pin): "RPC"
 *   ticker(pin): "TRRCX"
 *   fundName(pin): "Retirement 2030 Fund"
 *   formattedEntityNames(pin): "Anne A. Anlia"
 *   availableShares(pin): 1430.802
 *   }
 * }
 *
 */
export const getUserRetirementFunds = createSelector(
  [getUserFundsRaw],
  (funds) => funds?.[0]?.retirementAgreements ?? {}
);

/**
 * Simple selector to get object of non retirement agreements a client owns
 * @param state
 * @returns {nonRetirementAgreements | Object}
 * Same as retirement above
 *
 */
export const getUserNonRetirementFunds = createSelector(
  [getUserFundsRaw],
  (funds) => funds?.[0]?.nonRetirementAgreements ?? {}
);

/**
 * Factory method designed to take userFunds classifications and return whether or not the user owns funds in a
 * given classification
 * @param fundGroup - this takes an object map of agreement types found in userFunds
 * example: getUserRetirementFunds
 * @returns {Selector}
 *
 */
function hasFundsByAgreementFactory(fundGroup) {
  return createSelector(
    getUserRetirementFunds,
    getUserNonRetirementFunds,
    (retirementFunds, nonRetirementFunds) => {
      const funds =
        fundGroup === RETIREMENT ? retirementFunds : nonRetirementFunds;
      // probably more appropriate as a switch after we support more than two classifications
      const agreementTypes = keys(funds);
      const fundsByGroupList = agreementTypes.map((agreementType) => {
        if (funds[agreementType].length) {
          return funds[agreementType];
        }
        return null;
      });
      return Boolean(fundsByGroupList.length);
    }
  );
}

export const getRothIraFunds = createSelector(
  getUserRetirementFunds,
  getUserBusinessPartyId,
  (funds, BPID) => {
    const sortByFundName = (left, right) =>
      left.fundName.localeCompare(right.fundName);
    const rothList = funds.ROTH_IRA ? funds.ROTH_IRA : [];
    const newList = [...rothList.sort(sortByFundName)];
    return newList
      .filter((agreement) => {
        if (agreement?.agreementParties) {
          const ownerArr = agreement?.agreementParties.filter(
            (roles) => roles.roleCode === 'OWNR'
          );
          return ownerArr.length > 0
            ? ownerArr[0].businessPartyID === BPID
            : false;
        } else {
          return false;
        }
      })
      .map((fund) => ({
        ...fund,
        fundLimitMessage: getRothConversionFundLimitMessage(fund, rothList),
      }));
  }
);

export const getTraditionalOrRolloverIraFunds = createSelector(
  getUserRetirementFunds,
  getRothIraFunds,
  (funds, existingRothIRAFunds) => {
    const sortByFundName = (left, right) =>
      left.fundName.localeCompare(right.fundName);
    const traditionalList = funds.TRADITIONAL_IRA ? funds.TRADITIONAL_IRA : [];
    const rollOverList = funds.ROLLOVER_IRA ? funds.ROLLOVER_IRA : [];
    const newList = [
      ...traditionalList.sort(sortByFundName),
      ...rollOverList.sort(sortByFundName),
    ];
    return newList.map((fund) => ({
      ...fund,
      fundLimitMessage: getRothConversionFundLimitMessage(
        fund,
        existingRothIRAFunds
      ),
    }));
  }
);

export const getAllIRAFunds = createSelector(
  getUserRetirementFunds,
  getRothIraFunds,
  (funds, existingRothIRAFunds) => {
    const sortByFundName = (left, right) =>
      left.fundName.localeCompare(right.fundName);
    const traditionalList = funds.TRADITIONAL_IRA ? funds.TRADITIONAL_IRA : [];
    const rollOverList = funds.ROLLOVER_IRA ? funds.ROLLOVER_IRA : [];
    const rothList = funds.ROTH_IRA ? funds.ROTH_IRA : [];
    const rothRollOverList = funds.ROTH_ROLLOVER_IRA
      ? funds.ROTH_ROLLOVER_IRA
      : [];
    const newList = [
      ...traditionalList.sort(sortByFundName),
      ...rollOverList.sort(sortByFundName),
      ...rothList.sort(sortByFundName),
      ...rothRollOverList.sort(sortByFundName),
    ];
    return newList.map((fund) => ({
      ...fund,
      fundLimitMessage: getRothConversionFundLimitMessage(
        fund,
        existingRothIRAFunds
      ),
    }));
  }
);

/**
 * Simple selector to return if a user owns retirement funds (unrelated to eligibility)
 * @param state
 * @returns {Boolean}
 *
 */
export const getHasRetirementFunds = hasFundsByAgreementFactory(RETIREMENT);

/**
 * Simple selector to return if a user owns non-retirement funds (unrelated to eligibility)
 * @param state - Internally uses hasFundByAgreementFactory
 * @returns {Boolean}
 *
 */
export const getHasTaxableFunds = hasFundsByAgreementFactory(NON_RETIREMENT);

/**
 * Simple selector to return if a user owns non-retirement funds (unrelated to eligibility)
 * @param state - Internally uses hasFundByAgreementFactory
 * @returns {Date} (MM/DD/YYYY)
 *
 */
export const getBalanceAsOfDate = createSelector(
  [getUserFunds],
  (userFunds) => {
    const balance = userFunds.funds?.[0]?.balanceAsOfDate;
    return balance ? formatUnixDate(balance) : '';
  }
);

export const getUserOwnedIRAs = createSelector(
  [getAllIRAFunds, getUserBPID],
  (userFunds, BPID) => {
    return userFunds.some((fund) => {
      const ownerArr = fund.agreementParties
        ? fund.agreementParties.filter((roles) => roles.roleCode === 'OWNR')
        : [];
      return ownerArr.length > 0 && ownerArr[0].businessPartyID === BPID;
    });
  }
);

export const getUserOwnedIRAsFundList = createSelector(
  [getAllIRAFunds, getUserBPID],
  (userFunds, BPID) => {
    return userFunds.filter((fund) => {
      const ownerArr = fund.agreementParties
        ? fund.agreementParties.filter((roles) => roles.roleCode === 'OWNR')
        : [];
      return ownerArr.length > 0 && ownerArr[0].businessPartyID === BPID;
    });
  }
);
export const getUserNasuNewFunds = createSelector(
  [
    getUserNonRetirementFunds,
    getUserRetirementFunds,
    getJustPendingAgreementIDs,
  ],
  (nonRetirementUserFunds, retirementUserFunds, pendingFunds) => {
    const nonRetirementValues = Object.values(nonRetirementUserFunds);
    const retirementValues = Object.values(retirementUserFunds);
    const nonRetirementValues1D = _.flatten(nonRetirementValues);
    const retirementValues1D = _.flatten(retirementValues);
    let allFunds = nonRetirementValues1D.concat(retirementValues1D);
    allFunds = allFunds.filter((fund) => fund?.unfundedNASUAccount);
    allFunds = allFunds.filter((fund) =>
      pendingFunds.every((id) => id !== fund.agreementID)
    );
    return allFunds;
  }
);
